import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonTypeEnum,
  ButtonAlignEnum,
} from '@inter-ikea-kompis/component-button';
import Views from '../../settings/views/views';
import React from 'react';
import styles from './TopBar.module.less';
import platform from '../../util/platform';
import { IconButton } from '../IconButton';
import LanguageSelector from './LanguageSelector';
import UndoRedo from '../UndoRedo/UndoRedo';
import { KompisTooltip } from '@inter-ikea-kompis/react-components';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import {
  actionSetWallResizerInactive,
  actionSetProppingVisibility,
} from '../../state/scene';
import {
  selectIsMobile,
  selectIsMobilePortrait,
} from '../../state/userAgent/userAgentSelectors';
import { selectIsRtl } from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectIsWallResizerActive } from '../../state/scene/sceneSelectors';
import { selectCurrentView } from '../../state/navigation/navigationSelectors';
import { previousView } from '../../state/navigation';
import { thunkSetView } from '../../state/navigation/navigationThunks';
import views from '../../settings/views/views';
import { IconButtonSizeEnum } from '@inter-ikea-kompis/component-icon-button';
import loadProductMenu from '../../state/init/loadProductMenu';
import { useKioskIntegration } from '../../hooks/useKioskIntegration';

export interface Props {
  className?: any;
}

export const TopBar: React.FunctionComponent<Props> = ({ className }) => {
  const dispatch = useDispatch();

  const setWallResizerInactive = () =>
    dispatch(actionSetWallResizerInactive({ nonInteraction: true }));
  const _setProppingVisibility = () =>
    dispatch(actionSetProppingVisibility(true));
  const _previousView = () => dispatch(previousView());
  const goToStartView = () => dispatch(thunkSetView(views.START.name));
  const reloadProductMenu = () => dispatch(loadProductMenu());

  const isMobile = useSelector(selectIsMobile);
  const wallResizerActive = useSelector(selectIsWallResizerActive);
  const isMobilePortrait = useSelector(selectIsMobilePortrait);
  const isRtl = useSelector(selectIsRtl);
  const currentView = useSelector(selectCurrentView);
  const { integration, isUpptacka } = useKioskIntegration();

  const isKiosk = platform.isKiosk;

  const handleBackButtonClick = () => {
    integration?.backClicked();
    _previousView();
    _setProppingVisibility();
  };

  const startOver = () => {
    integration?.backClicked();
    goToStartView();
    reloadProductMenu();
  };

  const renderRestartButton = () => (
    <KompisTooltip text={translate(t.BACK)} theme={SkapaTheme}>
      <IconButton
        iconName={isRtl ? 'ArrowRightIcon' : 'ArrowLeftIcon'}
        label={translate(t.START_AGAIN)}
        onClick={startOver}
        theme={SkapaTheme}
        type={ButtonTypeEnum.secondary}
        size={IconButtonSizeEnum.small}
        ariaLabel={translate(t.BACK)}
        data-testid="start-again-button"
      />
    </KompisTooltip>
  );

  const onClick = () => {
    !isMobilePortrait && wallResizerActive && setWallResizerInactive();
  };

  const renderSceneViewTopBar = () => (
    <div className={styles.TopBar} onClick={onClick}>
      {renderRestartButton()}
      {isMobile && <div className={styles.divider} />}
      {isMobile && <UndoRedo />}
      {isKiosk && !isUpptacka && <LanguageSelector />}
    </div>
  );

  if (currentView === Views.SCENE.name) return renderSceneViewTopBar();

  return (
    <div className={classNames(styles.TopBar, className)}>
      <KompisTooltip text={translate(t.BACK)} theme={SkapaTheme}>
        <IconButton
          onClick={handleBackButtonClick}
          iconName={isRtl ? 'ArrowRightIcon' : 'ArrowLeftIcon'}
          type={ButtonTypeEnum.secondary}
          align-icon={ButtonAlignEnum.left}
          size={IconButtonSizeEnum.small}
          ariaLabel={translate(t.BACK)}
        />
      </KompisTooltip>
      {isKiosk && <LanguageSelector />}
    </div>
  );
};

export default TopBar;
